/**
 * Ensures that a URL or path ends with a trailing slash.
 * @param {string} url - The input URL or path.
 * @returns {string} The same value with a trailing slash, or "/" for invalid input.
 */
export const ensureTrailingSlash = url => {
  if (!url || typeof url !== "string") {
    return "/";
  } else {
    return url.endsWith("/") ? url : url + "/";
  }
};

/**
 * Ensures that a path has exactly one leading slash and no trailing slashes.
 * @param {string} path - The input path.
 * @returns {string} The normalized path, or an empty string for invalid input.
 */
export const ensureLeadingSlashOnly = path => {
  if (!path || typeof path !== "string" || path === "" || path === "/") {
    return "";
  } else {
    return "/" + (path || "").replace(/^\/+|\/+$/g, "");
  }
};

/**
 * Checks whether a URL already contains a valid http(s) or ftp(s) protocol.
 * @param {string} url - The URL to check.
 * @returns {boolean} True when the URL starts with a supported protocol.
 */
export const hasValidProtocol = url =>
  typeof url === "string" && (/^https?:\/\//i.test(url) || /^ftps?:\/\//i.test(url));

/**
 * Checks whether a string can be parsed as a valid URL.
 * @param {string} string - The string to validate.
 * @returns {boolean} True when the string is a valid URL.
 */
export const isValidUrl = string => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Normalizes website-like URLs by preserving http(s) URLs and prefixing
 * host-like values with a default protocol.
 * @param {string} url - The URL or host string to normalize.
 * @param {string} defaultProtocol - The protocol prefix to use when the URL has none.
 * @returns {string} A normalized URL, or an empty string when the value cannot be normalized.
 */
export const normalizeUrl = (url, defaultProtocol = "http://") => {
  if (typeof url !== "string" || !url.trim()) {
    return "";
  }

  const value = url.trim();

  // Keep URLs that already start with http(s).
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  // Accept protocol-relative URLs (for example //example.com) and prefix them.
  if (/^\/\//.test(value)) {
    return `${defaultProtocol.replace(/\/+$/, "")}${value}`;
  }

  // Prefix host-like inputs with the default protocol.
  const normalizedUrl = `${defaultProtocol}${value.replace(/^\/+/, "")}`;
  return isValidUrl(normalizedUrl) ? normalizedUrl : "";
};
